import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getFiltros } from '../../services/api';

// Componente de seleção múltipla com busca (reutilizado do Filtro.jsx)
const MultiSelect = ({ label, field, selectedValues, onChange, placeholder, optionsList, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        containerRef.current?.contains(event.target) ||
        dropdownRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
      setSearchTerm('');
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filtrar opções baseado no termo de busca
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return optionsList;
    const term = searchTerm.toLowerCase();
    return optionsList.filter((opt) => {
      const label = opt.label ?? opt.nome ?? opt.name ?? opt.descricao ?? '';
      return String(label).toLowerCase().includes(term);
    });
  }, [optionsList, searchTerm]);

  // Toggle seleção de um item
  const toggleOption = (value) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  // Remover um item selecionado
  const removeItem = (value, e) => {
    e.stopPropagation();
    onChange(selectedValues.filter((v) => v !== value));
  };

  // Limpar todos os selecionados
  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
    setSearchTerm('');
  };

  // Obter label de um valor
  const getLabel = (value) => {
    const opt = optionsList.find((o) => o.value === value);
    return opt ? (opt.label ?? opt.nome ?? opt.name ?? opt.descricao ?? value) : value;
  };

  // Calcular posição do dropdown com responsividade
  const getDropdownStyle = () => {
    if (!isOpen || !containerRef.current) return {};

    const rect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth < 640;
    const isTablet = viewportWidth >= 640 && viewportWidth < 1024;

    let left = rect.left;
    let width = Math.max(rect.width, isMobile ? 280 : 320);

    if (isMobile) {
      const maxWidth = viewportWidth - 16;
      width = Math.min(width, maxWidth);
      left = Math.max(8, Math.min(left, viewportWidth - width - 8));
    } else if (isTablet) {
      width = Math.min(width, viewportWidth * 0.6);
      if (left + width > viewportWidth - 16) {
        left = viewportWidth - width - 16;
      }
    }

    return {
      position: 'fixed',
      top: `${rect.bottom + 4}px`,
      left: `${left}px`,
      width: `${width}px`,
      maxWidth: isMobile ? 'calc(100vw - 16px)' : 'none',
      zIndex: 9999,
    };
  };

  return (
    <>
      <div className="flex flex-col flex-1 relative z-10 min-w-0" ref={containerRef}>
        <span className="text-xs text-gray-700 mb-1.5 whitespace-nowrap font-medium">{label}</span>
        <div className="relative">
          <div
            className={`relative flex items-center min-h-[42px] px-2 sm:px-3 py-2 pr-8 sm:pr-10 text-xs sm:text-sm bg-white border rounded-3xl cursor-pointer ${disabled ? 'border-gray-300 bg-gray-100' : 'border-[#f09005]'
              }`}
            onClick={() => {
              if (!disabled) {
                setIsOpen(true);
                setTimeout(() => inputRef.current?.focus(), 0);
              }
            }}
          >
            {selectedValues.length > 0 ? (
              <span className="text-gray-700 text-xs sm:text-sm truncate flex-1">
                {selectedValues.length === 1
                  ? getLabel(selectedValues[0])
                  : `${selectedValues.length} selecionados`}
              </span>
            ) : (
              <span className="text-gray-400 text-xs sm:text-sm truncate flex-1">{placeholder}</span>
            )}

            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {selectedValues.length > 0 && !disabled && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="p-0.5 sm:p-1 text-gray-400 hover:text-gray-600"
                  title="Limpar seleção"
                >
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              <button
                type="button"
                className={`p-0.5 sm:p-1 ${disabled ? 'text-gray-300' : 'text-[#f09005] hover:text-orange-600'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) {
                    setIsOpen(!isOpen);
                    if (!isOpen) {
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }
                  }
                }}
              >
                <svg className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {isOpen && !disabled && createPortal(
        <div
          ref={dropdownRef}
          className="bg-white border border-gray-300 rounded-lg shadow-2xl max-h-[60vh] sm:max-h-60 overflow-hidden"
          style={getDropdownStyle()}
        >
          <div className="p-1.5 sm:p-2 border-b border-gray-200">
            <input
              ref={inputRef}
              type="text"
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md outline-none focus:border-[#f09005]"
              placeholder="Digite para buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="max-h-[50vh] sm:max-h-44 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const value = opt.value ?? opt.id;
                const optLabel = opt.label ?? opt.nome ?? opt.name ?? opt.descricao;
                const isSelected = selectedValues.includes(value);

                return (
                  <div
                    key={value}
                    className={`flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 cursor-pointer hover:bg-orange-50 ${isSelected ? 'bg-orange-50' : ''
                      }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleOption(value);
                    }}
                  >
                    <div
                      className={`w-3.5 h-3.5 sm:w-4 sm:h-4 border rounded flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-[#f09005] border-[#f09005]' : 'border-gray-300'
                        }`}
                    >
                      {isSelected && (
                        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className="text-xs sm:text-sm text-gray-700 truncate">{optLabel}</span>
                  </div>
                );
              })
            ) : (
              <div className="px-2 sm:px-3 py-3 sm:py-4 text-xs sm:text-sm text-gray-500 text-center">
                {searchTerm ? 'Nenhum resultado encontrado' : 'Nenhuma opção disponível'}
              </div>
            )}
          </div>

          {selectedValues.length > 0 && (
            <div className="px-2 sm:px-3 py-1.5 sm:py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500">
              {selectedValues.length} {selectedValues.length === 1 ? 'item selecionado' : 'itens selecionados'}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

// Componente de seleção simples (para ano)
const SelectSimple = ({ label, field, value, onChange, placeholder, optionsList, disabled = false }) => {
  const selectId = `select-${field}`;

  const handleButtonClick = (e) => {
    e.preventDefault();
    const selectElement = document.getElementById(selectId);
    if (selectElement) {
      selectElement.focus();
      selectElement.click();
    }
  };

  const selectedOption = optionsList.find(opt => (opt.value ?? opt.id) === value);
  const displayValue = selectedOption ? (selectedOption.label ?? selectedOption.nome ?? selectedOption.name ?? selectedOption.descricao) : '';

  return (
    <div className="flex flex-col flex-1 relative z-10 min-w-0">
      <span className="text-xs text-gray-700 mb-1.5 whitespace-nowrap font-medium">{label}</span>
      <div className="relative flex items-center rounded-3xl border border-[#f09005] w-full flex-shrink-0 bg-white">
        <select
          id={selectId}
          className="flex-1 px-2 sm:px-3 py-2 sm:py-2.5 pr-8 sm:pr-10 text-xs sm:text-sm text-gray-700 bg-white border-0 outline-none appearance-none rounded-3xl cursor-pointer"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="">{placeholder}</option>
          {optionsList.map((opt) => (
            <option key={opt.value ?? opt.id} value={opt.value ?? opt.id}>
              {opt.label ?? opt.nome ?? opt.name ?? opt.descricao}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleButtonClick}
          className="absolute right-0 flex items-center justify-center w-7 sm:w-9 h-full bg-transparent text-[#f09005] hover:text-orange-600 transition-colors cursor-pointer pointer-events-auto"
          aria-label={`Abrir ${label}`}
          disabled={disabled}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const FiltroEvoVendas = ({ onChange }) => {
  const [loading, setLoading] = useState(false);

  const [options, setOptions] = useState({
    postos: [],
    estados: [],
    anos: [],
  });

  const [values, setValues] = useState({
    ano: [],
    posto: [],
    estado: [],
  });

  // OTIMIZAÇÃO: evitar chamada pesada só para descobrir anos (gera lista local)
  // Se precisar no futuro, podemos trocar por um endpoint leve de metadata.
  const buscarAnosDisponiveis = async () => {
    const anoAtual = new Date().getFullYear();
    const anoMin = 2013; // baseado no range visto nos dashboards
    const anos = [];
    for (let ano = anoAtual; ano >= anoMin; ano--) {
      anos.push({ value: String(ano), label: String(ano) });
    }
    return anos;
  };

  const carregarFiltros = async (filtrosAplicados = {}) => {
    try {
      setLoading(true);
      const resposta = await getFiltros(filtrosAplicados);

      const mapearOpcoes = (lista, campo) => {
        return (lista || []).map((item) => {
          const rawValue = item[campo] ?? item[`visao_geral.${campo}`] ?? Object.values(item)[0];
          return {
            value: rawValue,
            label: String(rawValue),
          };
        });
      };

      // Buscar anos disponíveis dos dados reais
      const anosDisponiveis = await buscarAnosDisponiveis();

      setOptions((prev) => ({
        postos: mapearOpcoes(resposta.filtro_posto, 'posto'),
        estados: mapearOpcoes(resposta.filtro_estado, 'estado'),
        anos: anosDisponiveis,
      }));
    } catch (error) {
      console.error('Erro ao carregar filtros:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carregar filtros iniciais
  useEffect(() => {
    carregarFiltros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recarregar postos quando estado mudar
  useEffect(() => {
    const carregarPostos = async (estados) => {
      if (!estados || estados.length === 0) {
        setOptions((prev) => ({ ...prev, postos: [] }));
        return;
      }

      try {
        const filtros = { 'visao_geral.estado': estados.join('|||') };
        const resposta = await getFiltros(filtros);

        const postos = (resposta.filtro_posto || []).map((item) => {
          const rawValue = item['visao_geral.posto'] ?? item.posto ?? Object.values(item)[0];
          return {
            value: rawValue,
            label: String(rawValue),
          };
        });

        setOptions((prev) => ({ ...prev, postos }));
      } catch (error) {
        console.error('Erro ao carregar postos:', error);
      }
    };

    carregarPostos(values.estado);
  }, [values.estado]);

  // Ref para debounce das atualizações de filtros
  const debounceTimeoutRef = useRef(null);
  const valoresAnterioresRef = useRef(null);

  useEffect(() => {
    const valoresIguais = valoresAnterioresRef.current &&
      JSON.stringify(values) === JSON.stringify(valoresAnterioresRef.current);

    if (valoresIguais) {
      return;
    }

    valoresAnterioresRef.current = JSON.parse(JSON.stringify(values));

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (onChange) {
        onChange(values);
      }
    }, 300);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [values, onChange]);

  // Handler para multi-select
  const handleMultiChange = (field) => (newValues) => {
    setValues((prev) => {
      const updated = { ...prev, [field]: newValues };

      // Limpar filtros dependentes quando o filtro pai muda
      if (field === 'estado') {
        updated.posto = [];
      }

      return updated;
    });
  };

  return (
    <div className="w-full flex flex-col bg-transparent relative z-10">
      {/* Filtros responsivos */}
      <div className="flex items-start gap-2 sm:gap-3 flex-wrap sm:flex-nowrap pb-2">
        <MultiSelect
          label="Ano"
          field="ano"
          selectedValues={values.ano}
          onChange={handleMultiChange('ano')}
          placeholder="Selecione o ano"
          optionsList={options.anos}
          disabled={loading}
        />

        <MultiSelect
          label="Posto"
          field="posto"
          selectedValues={values.posto}
          onChange={handleMultiChange('posto')}
          placeholder="Selecione o posto"
          optionsList={options.postos}
          disabled={loading}
        />

        <MultiSelect
          label="Região"
          field="estado"
          selectedValues={values.estado}
          onChange={handleMultiChange('estado')}
          placeholder="Selecione a região"
          optionsList={options.estados}
          disabled={loading}
        />
      </div>
    </div>
  );
};

export default FiltroEvoVendas;

